import { useEffect, useMemo, useRef, useState } from "react"
import type { MouseEvent } from "react"

import type {
  MapCoordinateV1,
  PropertyAddressResultV1,
  PropertyMapConfigurationV1,
  RouteInputsResultV1,
} from "@impetusos/contracts"

import {
  readPropertyMapConfiguration,
  propertyMapImageUrl,
  resolvePropertyAddress,
  resolveRouteInputs,
} from "../transport/index.js"

const WIDTH = 900
const HEIGHT = 500
const ZOOM = 19
const EARTH_RADIUS_METERS = 6_378_137
const SQ_M_TO_SQ_FT = 10.76391041671

const worldPoint = (coordinate: MapCoordinateV1) => {
  const size = 512 * 2 ** ZOOM
  const latitude = Math.max(-85.051129, Math.min(85.051129, coordinate.latitude))
  const sin = Math.sin(latitude * Math.PI / 180)
  return {
    x: (coordinate.longitude + 180) / 360 * size,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * size,
  }
}

const screenPoint = (
  coordinate: MapCoordinateV1,
  center: MapCoordinateV1,
) => {
  const point = worldPoint(coordinate)
  const origin = worldPoint(center)
  return { x: point.x - origin.x + WIDTH / 2, y: point.y - origin.y + HEIGHT / 2 }
}

const coordinateAt = (
  x: number,
  y: number,
  center: MapCoordinateV1,
): MapCoordinateV1 => {
  const size = 512 * 2 ** ZOOM
  const origin = worldPoint(center)
  const worldX = origin.x + x - WIDTH / 2
  const worldY = origin.y + y - HEIGHT / 2
  const longitude = worldX / size * 360 - 180
  const n = Math.PI - 2 * Math.PI * worldY / size
  return {
    longitude,
    latitude: 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))),
  }
}

const areaSqFt = (points: readonly MapCoordinateV1[]): number => {
  if (points.length < 3) return 0
  let area = 0
  for (let index = 0; index < points.length; index += 1) {
    const point = points[index]!
    const following = points[(index + 1) % points.length]!
    const delta = (following.longitude - point.longitude) * Math.PI / 180
    area += delta * (
      2
      + Math.sin(point.latitude * Math.PI / 180)
      + Math.sin(following.latitude * Math.PI / 180)
    )
  }
  return Math.round(
    Math.abs(area * EARTH_RADIUS_METERS ** 2 / 2) * SQ_M_TO_SQ_FT,
  )
}

type WorkspaceState =
  | { status: "loading" }
  | { status: "unavailable"; reason: string }
  | {
      status: "ready"
      map: PropertyMapConfigurationV1
      address: Extract<PropertyAddressResultV1, { status: "available" }>
      route: RouteInputsResultV1
    }

const unavailableReason = (reason: string): string => ({
  ambiguous: "More than one address matched. Confirm the exact Service Location before measuring.",
  no_match: "No exact address matched. Confirm the Service Location or use an onsite measurement.",
  invalid_input: "The Service Location address is incomplete or invalid.",
  rate_limited: "The mapping provider is busy. Retry later or use an onsite measurement.",
  quota_exceeded: "Map-assisted measurement is temporarily unavailable.",
  provider_unavailable: "The mapping provider is temporarily unavailable.",
  incompatible: "The mapping provider returned an unsupported result.",
}[reason] ?? "Map-assisted measurement is unavailable.")

export const PropertyMeasurementWorkspace = () => {
  const [state, setState] = useState<WorkspaceState>({ status: "loading" })
  const [points, setPoints] = useState<MapCoordinateV1[]>([])
  const [exclusionArea, setExclusionArea] = useState(0)
  const overlay = useRef<SVGSVGElement>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const [map, address] = await Promise.all([
          readPropertyMapConfiguration(),
          resolvePropertyAddress({
            addressLine1: "2611 N 12th St",
            locality: "Tampa",
            region: "FL",
            postalCode: "33605",
            countryCode: "US",
            permanent: true,
          }),
        ])
        if (address.status !== "available") {
          if (!cancelled) setState({ status: "unavailable", reason: address.reason })
          return
        }
        const route = await resolveRouteInputs({
          profile: "driving",
          coordinates: [
            address.coordinate,
            {
              longitude: address.coordinate.longitude + 0.01,
              latitude: address.coordinate.latitude + 0.01,
            },
          ],
        })
        if (!cancelled) {
          setState({ status: "ready", map, address, route })
          const center = address.coordinate
          setPoints([
            { longitude: center.longitude - 0.00035, latitude: center.latitude - 0.0003 },
            { longitude: center.longitude + 0.00035, latitude: center.latitude - 0.0003 },
            { longitude: center.longitude + 0.00035, latitude: center.latitude + 0.0003 },
            { longitude: center.longitude - 0.00035, latitude: center.latitude + 0.0003 },
          ])
        }
      } catch {
        if (!cancelled) setState({ status: "unavailable", reason: "provider_unavailable" })
      }
    }
    void load()
    return () => { cancelled = true }
  }, [])

  const baseArea = useMemo(() => areaSqFt(points), [points])
  const serviceableArea = Math.max(baseArea - exclusionArea, 0)
  if (state.status === "loading") {
    return (
      <section className="property-measurement-workspace" id="property-measurement">
        <div className="journey-message"><span className="spinner" />Loading permanent address and satellite map…</div>
      </section>
    )
  }
  if (state.status === "unavailable") {
    return (
      <section className="property-measurement-workspace" id="property-measurement">
        <div className="empty-state">
          <span className="state-symbol">↻</span>
          <h3>Map-assisted measurement unavailable</h3>
          <p>{unavailableReason(state.reason)}</p>
          <p>Nothing was selected or saved. Onsite/manual measurement remains available.</p>
        </div>
      </section>
    )
  }
  const center = state.address.coordinate
  const staticMap = propertyMapImageUrl(state.map, center, ZOOM)
  const routeSummary = state.route.status === "available"
    ? `${Math.round(state.route.route.distanceMeters / 160.934) / 10} mi · ${Math.ceil(state.route.route.durationSeconds / 60)} min`
    : `Route ${state.route.reason.replaceAll("_", " ")}`
  const polygon = points
    .map((point) => {
      const pixel = screenPoint(point, center)
      return `${pixel.x},${pixel.y}`
    })
    .join(" ")

  const addPoint = (event: MouseEvent<SVGSVGElement>) => {
    if (points.length >= 20 || overlay.current === null) return
    const bounds = overlay.current.getBoundingClientRect()
    const x = (event.clientX - bounds.left) / bounds.width * WIDTH
    const y = (event.clientY - bounds.top) / bounds.height * HEIGHT
    setPoints((current) => [...current, coordinateAt(x, y, center)])
  }

  return (
    <section className="property-measurement-workspace" id="property-measurement" aria-labelledby="property-title">
      <header className="property-heading">
        <div>
          <p className="eyebrow">Property mapping · permanent result</p>
          <h2 id="property-title">Satellite measurement workspace</h2>
          <p>{state.address.formattedAddress}</p>
        </div>
        <div className="mapping-badges">
          <span>Rooftop address</span>
          <span>{routeSummary}</span>
        </div>
      </header>
      <div className="property-layout">
        <div className="map-panel">
          <img src={staticMap} alt="Mapbox satellite view centered on the selected Service Location" />
          <svg
            ref={overlay}
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            role="img"
            tabIndex={0}
            aria-label="Editable serviceable-area polygon"
            onClick={addPoint}
            onKeyDown={(event) => {
              if ((event.key === "Enter" || event.key === " ") && points.length < 20) {
                event.preventDefault()
                setPoints((current) => [...current, center])
              }
            }}
          >
            {points.length >= 3 && <polygon points={polygon} />}
            {points.map((point, index) => {
              const pixel = screenPoint(point, center)
              return <circle key={`${point.longitude}-${point.latitude}`} cx={pixel.x} cy={pixel.y} r="7"><title>Boundary point {index + 1}</title></circle>
            })}
          </svg>
          <div className="map-attribution">
            <a href={state.map.attribution.mapbox}>© Mapbox</a>
            <a href={state.map.attribution.openStreetMap}>© OpenStreetMap</a>
            <a href={state.map.attribution.improveThisMap}>Improve this map</a>
          </div>
        </div>
        <aside className="measurement-panel">
          <div>
            <p className="eyebrow">Reproducible preview</p>
            <strong className="area-reading">{serviceableArea.toLocaleString()} <small>sq ft</small></strong>
          </div>
          <dl>
            <div><dt>Polygon area</dt><dd>{baseArea.toLocaleString()} sq ft</dd></div>
            <div><dt>Exclusions</dt><dd>−{exclusionArea.toLocaleString()} sq ft</dd></div>
            <div><dt>Formula</dt><dd>serviceable-area-v1</dd></div>
          </dl>
          <label>
            <span>Manual exclusion area (sq ft)</span>
            <input
              type="number"
              min="0"
              max={baseArea}
              value={exclusionArea}
              onChange={(event) => {
                if (event.target.value === "") {
                  setExclusionArea(0)
                  return
                }
                const next = Number(event.target.value)
                if (Number.isFinite(next)) setExclusionArea(Math.max(0, next))
              }}
            />
          </label>
          <div className="measurement-actions">
            <button type="button" className="secondary-button" onClick={() => setPoints([])}>Clear polygon</button>
            <span>{points.length} boundary points · click map to add</span>
          </div>
          <p className="mapping-note">
            Preview is advisory. Frappe recomputes and hashes every verified snapshot before commit.
          </p>
        </aside>
      </div>
    </section>
  )
}
